import React, { useEffect, useState, useRef, Component } from 'react';
import { Play, ChevronLeft, ChevronRight, Volume2, X, Hand, Droplet, ClipboardList, MessageCircle, HelpCircle, Globe } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
import { Monitor, Smartphone, Lightbulb } from 'lucide-react';
// --- Types & Constants ---
type Step = 'format' | 'language' | 'intro' | 'video' | 'info' | 'reminders' | 'goodbye';
type FaqState = 'none' | 'menu' | 'answer';
type Language = 'es' | 'en';
type Format = 'mobile' | 'desktop';
const IMAGES = {
  startBg: 'https://cdn.magicpatterns.com/uploads/881jkJEAqmuZwxE7RaybGa/Screenshot_2026-06-19_at_09.29.53.png',
  introVideoBg: 'https://cdn.magicpatterns.com/uploads/aQvZ9JfcxfY86RyhBophG4/App%20Previewlong.mp4',
  handsWarm: 'https://cdn.magicpatterns.com/uploads/i8SWQ7fArduUdFHejCXgu1/Screenshot_2026-06-19_at_08.57.24.png',
  videoPoster: 'https://cdn.magicpatterns.com/uploads/gX8cCuRy5nsdX3YVpcSc7T/Screenshot_2026-06-19_at_08.56.57.png',
  videoUrl: 'https://cdn.magicpatterns.com/uploads/iSb8fmZ9hzTzoxqFT8mZGv/App%20Previewlong.mp4',
  desktopVideoUrl: 'https://cdn.magicpatterns.com/uploads/tB9PzngkHE61pF9FsgJSEg/16-9.mp4'
};
const FAQS = {
  es: ['¿Puedo lavarme las manos normalmente?', '¿Qué jabones debería evitar?', '¿Debo usar guantes protectores en el trabajo?', '¿Puedo usar gel hidroalcohólico?', '¿Puedo usar cosméticos o productos para las uñas?', '¿Puedo nadar?', '¿Puedo tomar el sol?'],
  en: ['Can I wash my hands normally?', 'Which soaps should I avoid?', 'Should I wear protective gloves at work?', 'Can I use hand sanitiser?', 'Can I use cosmetics or nail products?', 'Can I swim?', 'Can I go out in the sun?']
};
const TRANSLATIONS = {
  es: {
    langSelect: 'Elige tu idioma',
    introTitle1: 'Cómo aplicar',
    introTitle2: 'en las manos',
    introBody: 'Su médico le ha indicado ANZUPGO® para el tratamiento del eccema crónico de manos. En esta breve guía verá cómo aplicar la crema correctamente y encontrará respuestas a preguntas frecuentes.',
    startBtn: 'Empezar',
    disclaimer: 'Este contenido no sustituye las indicaciones de su médico.',
    videoLabel: 'Vídeo de ejemplo',
    videoSub: 'Antes de aplicar ANZUPGO®, asegúrese de que sus manos estén limpias y secas.',
    faqBtn: 'FAQ',
    faqHint: 'Pulse FAQ en cualquier momento para ver respuestas a preguntas frecuentes.',
    faqTitle: 'Preguntas frecuentes',
    faqSubtitle: 'Haga clic en la pregunta que desea responder',
    faqAnswer: 'Sí. Antes de aplicar ANZUPGO®, las manos deben estar limpias y secas. Siga siempre las indicaciones de su médico sobre cuándo y cómo aplicar la crema durante el día.',
    understoodBtn: 'Entendido',
    backToFaqBtn: 'Volver a preguntas',
    infoTitle: 'Información importante sobre ANZUPGO®',
    infoBody: 'ANZUPGO® es una crema indicada para adultos con eccema crónico de manos de moderado a grave cuando los corticosteroides tópicos no son adecuados o no son suficientes. Debe utilizarse siguiendo las indicaciones de su médico.',
    infoCards: [{
      title: 'Sin corticosteroides',
      desc: 'Crema libre de esteroides.'
    }, {
      title: 'Aplicación tópica',
      desc: 'Se aplica directamente sobre las zonas afectadas.'
    }, {
      title: 'Uso según indicación médica',
      desc: 'Siga siempre la pauta recomendada por su médico.'
    }, {
      title: 'Consulte si tiene dudas',
      desc: 'Su médico o farmacéutico pueden orientarle.'
    }],
    continueBtn: 'Continuar',
    remindersTitle: 'Tres cosas que debe recordar',
    reminders: [{
      title: 'Manos limpias y secas',
      desc: 'Antes de aplicar la crema, asegúrese de que sus manos estén limpias y secas.'
    }, {
      title: 'Siga la pauta indicada',
      desc: 'Aplique ANZUPGO® solo como le ha indicado su médico.'
    }, {
      title: 'No cambie el tratamiento por su cuenta',
      desc: 'Si tiene dudas, hable con su médico o farmacéutico.'
    }],
    goodbyeTitle: 'Gracias por ver esta guía',
    goodbyeBody: 'Si tiene más preguntas sobre ANZUPGO® o sobre el cuidado de sus manos, hable con su médico o farmacéutico.',
    goodbyeDisclaimer: 'Este contenido es solo informativo y no sustituye el consejo médico profesional.',
    finishBtn: 'Finalizar'
  },
  en: {
    langSelect: 'Choose your language',
    introTitle1: 'How to apply',
    introTitle2: 'to your hands',
    introBody: 'Your doctor has prescribed ANZUPGO® for the treatment of chronic hand eczema. In this short guide, you will see how to apply the cream correctly and find answers to frequently asked questions.',
    startBtn: 'Start',
    disclaimer: "This content does not replace your doctor's instructions.",
    videoLabel: 'Example video',
    videoSub: 'Before applying ANZUPGO®, make sure your hands are clean and dry.',
    faqBtn: 'FAQ',
    faqHint: 'Tap FAQ at any time to see answers to frequently asked questions.',
    faqTitle: 'Frequently asked questions',
    faqSubtitle: 'Click on the question you would like answered',
    faqAnswer: "Yes. Before applying ANZUPGO®, your hands must be clean and dry. Always follow your doctor's instructions on when and how to apply the cream during the day.",
    understoodBtn: 'Understood',
    backToFaqBtn: 'Back to questions',
    infoTitle: 'Important information about ANZUPGO®',
    infoBody: 'ANZUPGO® is a cream indicated for adults with moderate to severe chronic hand eczema when topical corticosteroids are inadequate or inappropriate. It should be used as directed by your doctor.',
    infoCards: [{
      title: 'Steroid-free',
      desc: 'Steroid-free cream.'
    }, {
      title: 'Topical application',
      desc: 'Applied directly to the affected areas.'
    }, {
      title: 'Use as directed',
      desc: 'Always follow the schedule recommended by your doctor.'
    }, {
      title: 'Ask if you have doubts',
      desc: 'Your doctor or pharmacist can guide you.'
    }],
    continueBtn: 'Continue',
    remindersTitle: 'Three things to remember',
    reminders: [{
      title: 'Clean and dry hands',
      desc: 'Before applying the cream, make sure your hands are clean and dry.'
    }, {
      title: 'Follow the indicated schedule',
      desc: 'Apply ANZUPGO® only as directed by your doctor.'
    }, {
      title: 'Do not change the treatment on your own',
      desc: 'If you have doubts, talk to your doctor or pharmacist.'
    }],
    goodbyeTitle: 'Thank you for watching this guide',
    goodbyeBody: 'If you have more questions about ANZUPGO® or caring for your hands, talk to your doctor or pharmacist.',
    goodbyeDisclaimer: 'This content is for informational purposes only and does not replace professional medical advice.',
    finishBtn: 'Finish'
  }
};
// --- Main App Component ---
export function App() {
  const init = useScreenInit();
  const [step, setStep] = useState<Step>(init?.step || 'format');
  const [lang, setLang] = useState<Language>(init?.lang || 'es');
  const [format, setFormat] = useState<Format>(init?.format || 'mobile');
  const [faqState, setFaqState] = useState<FaqState>('none');
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const t = TRANSLATIONS[lang];
  const faqs = FAQS[lang];
  const goNext = (nextStep: Step) => {
    setStep(nextStep);
    window.scrollTo(0, 0);
  };
  const goBack = (prevStep: Step) => {
    setStep(prevStep);
    window.scrollTo(0, 0);
  };
  const isDesktop = format === 'desktop';
  return <div className="min-h-screen bg-black flex justify-center items-center font-sans text-white selection:bg-[#82D600] selection:text-black p-4" data-id="element-0">
      {/* Container */}
      <div className={`bg-[#1a1a1a] relative overflow-hidden flex flex-col shadow-2xl transition-all duration-500 ${isDesktop ? 'w-full max-w-5xl aspect-video rounded-2xl border border-zinc-800' : 'w-full max-w-md h-[100dvh] sm:h-[850px] sm:rounded-3xl sm:border sm:border-zinc-800'}`} data-id="element-1">
        {/* Render Current Screen */}
        {step === 'format' && <FormatSelectionScreen onSelect={selectedFormat => {
        setFormat(selectedFormat);
        goNext('language');
      }} data-id="element-2" />}
        {step === 'language' && <LanguageScreen onSelect={selectedLang => {
        setLang(selectedLang);
        goNext('intro');
      }} onBack={() => goBack('format')} data-id="element-3" />}
        {step === 'intro' && <IntroScreen t={t} onNext={() => goNext('video')} onBack={() => goBack('language')} isDesktop={isDesktop} data-id="element-4" />}
        {step === 'video' && <VideoScreen t={t} faqs={faqs} onNext={() => goNext('reminders')} onBack={() => goBack('intro')} faqState={faqState} setFaqState={setFaqState} selectedFaqIndex={selectedFaqIndex} setSelectedFaqIndex={setSelectedFaqIndex} isDesktop={isDesktop} data-id="element-5" />}
        {step === 'reminders' && <RemindersScreen t={t} onNext={() => goNext('info')} onBack={() => goBack('video')} isDesktop={isDesktop} data-id="element-6" />}
        {step === 'info' && <InfoScreen t={t} onNext={() => goNext('goodbye')} onBack={() => goBack('reminders')} isDesktop={isDesktop} data-id="element-7" />}
        {step === 'goodbye' && <GoodbyeScreen t={t} onFinish={() => goNext('format')} onBack={() => goBack('info')} isDesktop={isDesktop} setFaqState={setFaqState} data-id="element-8" />}

        {/* Global FAQ Overlays */}
        <FaqOverlay faqState={faqState} setFaqState={setFaqState} selectedFaqIndex={selectedFaqIndex} setSelectedFaqIndex={setSelectedFaqIndex} t={t} faqs={faqs} data-id="element-9" />
      </div>
    </div>;
} // --- Screen Components ---
function FaqOverlay({
  faqState,
  setFaqState,
  selectedFaqIndex,
  setSelectedFaqIndex,
  t,
  faqs
}: any) {
  if (faqState === 'none') return null;
  return <>
      {faqState === 'menu' && <div className="absolute inset-0 z-50 flex justify-end pointer-events-auto" data-id="element-10">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setFaqState('none')} data-id="element-11" />
          <div className="relative w-[85%] sm:w-96 bg-[#1a1a1a] h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-zinc-800" data-id="element-12">
            <div className="p-6 flex justify-between items-start border-b border-zinc-800" data-id="element-13">
              <div data-id="element-14">
                <h2 className="text-xl font-semibold mb-1" data-id="element-15">{t.faqTitle}</h2>
                <p className="text-xs text-zinc-400" data-id="element-16">{t.faqSubtitle}</p>
              </div>
              <button onClick={() => setFaqState('none')} className="p-2 bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition shrink-0" data-id="element-17">
                <X className="w-5 h-5" data-id="element-18" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-19">
              {faqs.map((faq: string, idx: number) => <button key={idx} onClick={() => {
            setSelectedFaqIndex(idx);
            setFaqState('answer');
          }} className={`w-full text-left p-4 bg-[#2a2a2a] rounded-2xl hover:bg-zinc-700 transition-colors text-sm font-medium leading-snug border border-transparent hover:border-[#82D600]/50 ${idx === 0 || idx === 2 ? 'animate-pulse border-[#82D600]/30' : ''}`} data-id="element-20">
                  {faq}
                </button>)}
            </div>
          </div>
        </div>}

      {faqState === 'answer' && selectedFaqIndex !== null && <div className="absolute inset-0 z-[60] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200 pointer-events-auto" data-id="element-21">
          <div className="bg-[#2a2a2a] rounded-3xl p-6 sm:p-8 w-full max-w-md border border-[#82D600]/20 shadow-2xl shadow-[#82D600]/10" data-id="element-22">
            <div className="w-12 h-12 bg-[#82D600]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6" data-id="element-23">
              <HelpCircle className="w-6 h-6 text-[#82D600]" data-id="element-24" />
            </div>

            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 leading-tight" data-id="element-25">
              {faqs[selectedFaqIndex]}
            </h3>

            <p className="text-zinc-300 mb-8 leading-relaxed text-base sm:text-lg" data-id="element-26">
              {t.faqAnswer}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-27">
              <button onClick={() => setFaqState('none')} className="flex-1 bg-[#82D600] text-black font-semibold py-3.5 rounded-xl hover:bg-[#93e815] transition-colors" data-id="element-28">
                {t.understoodBtn}
              </button>
              <button onClick={() => setFaqState('menu')} className="flex-1 bg-transparent border border-zinc-600 text-zinc-300 font-medium py-3.5 rounded-xl hover:text-white hover:border-zinc-400 transition-colors" data-id="element-29">
                {t.backToFaqBtn}
              </button>
            </div>
          </div>
        </div>}
    </>;
}
function FormatSelectionScreen({
  onSelect
}: {
  onSelect: (format: Format) => void;
}) {
  const [showStrategy, setShowStrategy] = useState(false);
  return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a] p-6" data-id="element-30">
      <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md mx-auto w-full" data-id="element-31">
        <h2 className="text-3xl font-semibold mb-12" data-id="element-32">Select Format</h2>

        <div className="w-full space-y-4 flex flex-col sm:flex-row sm:space-y-0 sm:space-x-4 mb-12" data-id="element-33">
          <button onClick={() => onSelect('mobile')} className="flex-1 bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg py-8 rounded-2xl hover:bg-zinc-800 hover:border-[#82D600]/50 transition-colors flex flex-col items-center justify-center gap-4" data-id="element-34">
            <Smartphone className="w-12 h-12 text-[#82D600]" data-id="element-35" />
            Mobile (9:16)
          </button>
          <button onClick={() => onSelect('desktop')} className="flex-1 bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg py-8 rounded-2xl hover:bg-zinc-800 hover:border-[#82D600]/50 transition-colors flex flex-col items-center justify-center gap-4" data-id="element-36">
            <Monitor className="w-12 h-12 text-[#82D600]" data-id="element-37" />
            Desktop (16:9)
          </button>
        </div>

        <button onClick={() => setShowStrategy(true)} className="bg-transparent border border-zinc-700 text-zinc-400 font-medium py-3 px-6 rounded-full hover:text-white hover:border-zinc-500 transition-colors flex items-center gap-2" data-id="element-38">
          <Lightbulb className="w-4 h-4" data-id="element-39" />
          Strategy
        </button>
      </div>

      {/* Strategy Modal */}
      {showStrategy && <div className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200" data-id="element-40">
          <div className="bg-[#2a2a2a] rounded-3xl p-8 w-full max-w-lg border border-[#82D600]/20 shadow-2xl shadow-[#82D600]/10 flex flex-col max-h-[90%]" data-id="element-41">
            <div className="flex justify-between items-start mb-6" data-id="element-42">
              <div className="w-12 h-12 bg-[#82D600]/10 rounded-full flex items-center justify-center" data-id="element-43">
                <Lightbulb className="w-6 h-6 text-[#82D600]" data-id="element-44" />
              </div>
              <button onClick={() => setShowStrategy(false)} className="p-2 bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition" data-id="element-45">
                <X className="w-5 h-5" data-id="element-46" />
              </button>
            </div>

            <h3 className="text-2xl font-semibold mb-6 leading-tight" data-id="element-47">
              Strategy
            </h3>

            <div className="overflow-y-auto pr-2 space-y-4 text-zinc-300 leading-relaxed text-left" data-id="element-48">
              <p data-id="element-49">
                LEO Spain asked to make the ANZUPGO® application video from the
                HCP Snak™ available to patients.
              </p>
              <p data-id="element-50">
                Instead of simply extracting the video, we recommend turning it
                into a dedicated patient-facing guide. The video remains the
                core asset, but the experience is adapted to the patient context
                with a short introduction, Spanish voice-over and subtitles,
                optional FAQ support, key reminders, important product
                information, and a clear recommendation to speak to a doctor or
                pharmacist if unsure.
              </p>
              <p data-id="element-51">
                This makes the asset more relevant for patients scanning a QR
                code in the clinic: practical, reassuring, easy to follow, and
                better suited to supporting correct use of ANZUPGO®.
              </p>
            </div>

            <button onClick={() => setShowStrategy(false)} className="w-full mt-8 bg-[#82D600] text-black font-semibold py-3.5 rounded-xl hover:bg-[#93e815] transition-colors" data-id="element-52">
              Close
            </button>
          </div>
        </div>}
    </div>;
}
function PageNav({
  current,
  total
}: {
  current: number;
  total: number;
  onNext?: () => void;
  onBack?: () => void;
}) {
  return <div className="absolute top-0 left-0 right-0 p-4 z-40 flex justify-center items-center bg-gradient-to-b from-black/50 to-transparent" data-id="element-53">
      <div className="bg-black/40 backdrop-blur px-3 py-1 rounded-full text-xs font-medium tracking-widest text-zinc-300" data-id="element-54">
        {current} / {total}
      </div>
    </div>;
}
function LanguageScreen({
  onSelect,
  onBack
}: {
  onSelect: (lang: Language) => void;
  onBack: () => void;
}) {
  return <div className="relative w-full h-full flex flex-col p-6" data-id="element-55">
      <button onClick={onBack} className="absolute top-4 left-4 p-2 bg-black/40 backdrop-blur rounded-full text-white hover:bg-black/60 transition z-30" data-id="element-56">
        <ChevronLeft className="w-6 h-6" data-id="element-57" />
      </button>
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 bg-cover bg-center z-0" style={{
      backgroundImage: `url(${IMAGES.startBg})`
    }} data-id="element-58" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/80 to-transparent z-10" data-id="element-59" />

      <div className="relative z-20 flex-1 flex flex-col items-center justify-center text-center max-w-xs mx-auto w-full" data-id="element-60">
        <div className="w-20 h-20 bg-[#82D600]/10 rounded-full flex items-center justify-center mb-8 backdrop-blur-sm" data-id="element-61">
          <Globe className="w-10 h-10 text-[#82D600]" data-id="element-62" />
        </div>

        <h2 className="text-2xl font-semibold mb-2" data-id="element-63">Choose your language</h2>
        <h2 className="text-2xl font-semibold mb-12 text-zinc-400" data-id="element-64">
          Elige tu idioma
        </h2>

        <div className="w-full space-y-4" data-id="element-65">
          <button onClick={() => onSelect('en')} className="w-full bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg py-5 rounded-2xl hover:bg-zinc-800 hover:border-[#82D600]/50 transition-colors flex items-center justify-center gap-3" data-id="element-66">
            English
          </button>
          <button onClick={() => onSelect('es')} className="w-full bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg py-5 rounded-2xl hover:bg-zinc-800 hover:border-[#82D600]/50 transition-colors flex items-center justify-center gap-3" data-id="element-67">
            Español
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-20 mt-auto pt-8 border-t border-zinc-800/50" data-id="element-68">
        <div className="flex justify-between items-end opacity-80" data-id="element-69">
          <div className="text-xl font-bold tracking-widest text-zinc-400" data-id="element-70">
            LEO
          </div>
          <div className="text-xl font-bold" data-id="element-71">
            ANZUPGO<sup className="text-xs" data-id="element-72">®</sup>
          </div>
        </div>
      </div>
    </div>;
}
function IntroScreen({
  t,
  onNext,
  onBack,
  isDesktop
}: {
  t: any;
  onNext: () => void;
  onBack: () => void;
  isDesktop?: boolean;
}) {
  if (isDesktop) {
    return <div className="relative w-full h-full flex flex-row" data-id="element-73">
        {/* Left Side: Content */}
        <div className="w-1/2 h-full bg-[#1a1a1a] flex flex-col p-8 relative z-20 shadow-2xl" data-id="element-74">
          <PageNav current={1} total={5} onBack={onBack} onNext={onNext} data-id="element-75" />

          <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full mt-8" data-id="element-76">
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-4 lg:mb-6" data-id="element-77">
              {t.introTitle1} <br data-id="element-78" />
              <span className="font-semibold" data-id="element-79">ANZUPGO®</span> {t.introTitle2}
            </h1>
            <p className="text-zinc-300 text-lg lg:text-xl mb-8 lg:mb-12 leading-relaxed" data-id="element-80">
              {t.introBody}
            </p>

            <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg lg:text-xl py-4 lg:py-5 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-81">
              {t.startBtn}
              <ChevronRight className="w-6 h-6" data-id="element-82" />
            </button>

            <p className="text-zinc-500 text-xs lg:text-sm text-center mt-6 lg:mt-8" data-id="element-83">
              {t.disclaimer}
            </p>
          </div>

          {/* Logos */}
          <div className="flex justify-between items-end mt-auto opacity-80 pt-4 lg:pt-8" data-id="element-84">
            <div className="text-xl lg:text-2xl font-bold tracking-widest text-zinc-400" data-id="element-85">
              LEO
            </div>
            <div className="text-xl lg:text-2xl font-bold" data-id="element-86">
              ANZUPGO<sup className="text-xs lg:text-sm" data-id="element-87">®</sup>
            </div>
          </div>
        </div>

        {/* Right Side: Video */}
        <div className="w-1/2 h-full relative" data-id="element-88">
          <video src={IMAGES.desktopVideoUrl} className="w-full h-full object-cover" autoPlay playsInline loop muted data-id="element-89" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] to-transparent w-32" data-id="element-90" />
        </div>
      </div>;
  }
  return <div className="relative w-full h-full flex flex-col" data-id="element-91">
      {/* Background Video with Gradient Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-92">
        <video src={IMAGES.introVideoBg} className="w-full h-full object-cover" autoPlay playsInline loop muted data-id="element-93" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/80 to-transparent z-10" data-id="element-94" />

      <PageNav current={1} total={5} onBack={onBack} onNext={onNext} data-id="element-95" />

      {/* Content */}
      <div className="relative z-20 flex flex-col h-full p-6 justify-end pb-12" data-id="element-96">
        <h1 className="text-4xl font-light leading-tight mb-4" data-id="element-97">
          {t.introTitle1} <br data-id="element-98" />
          <span className="font-semibold" data-id="element-99">ANZUPGO®</span> {t.introTitle2}
        </h1>
        <p className="text-zinc-300 text-lg mb-8 leading-relaxed" data-id="element-100">
          {t.introBody}
        </p>

        <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors active:scale-[0.98]" data-id="element-101">
          {t.startBtn}
          <ChevronRight className="w-5 h-5" data-id="element-102" />
        </button>

        <p className="text-zinc-500 text-xs text-center mt-6" data-id="element-103">{t.disclaimer}</p>

        {/* Logos */}
        <div className="flex justify-between items-end mt-8 opacity-80" data-id="element-104">
          <div className="text-xl font-bold tracking-widest text-zinc-400" data-id="element-105">
            LEO
          </div>
          <div className="text-xl font-bold" data-id="element-106">
            ANZUPGO<sup className="text-xs" data-id="element-107">®</sup>
          </div>
        </div>
      </div>
    </div>;
}
function VideoScreen({
  t,
  faqs,
  onNext,
  onBack,
  faqState,
  setFaqState,
  selectedFaqIndex,
  setSelectedFaqIndex,
  isDesktop
}: any) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [subtitleIndex, setSubtitleIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };
  // Mock-up subtitles that change over time
  useEffect(() => {
    const interval = setInterval(() => {
      setSubtitleIndex(prev => (prev + 1) % 3);
    }, 4000); // Change subtitle every 4 seconds
    return () => clearInterval(interval);
  }, []);
  const mockSubtitles = [t.videoSub, 'Apply a thin layer to the affected areas.', 'Gently rub it in until fully absorbed.'];
  if (isDesktop) {
    return <div className="relative w-full h-full flex flex-col bg-black" data-id="element-108">
        <PageNav current={2} total={5} onBack={onBack} onNext={faqState === 'none' ? onNext : undefined} data-id="element-109" />

        {/* Full screen video for desktop */}
        <div className="absolute inset-0" data-id="element-110">
          <video ref={videoRef} src={IMAGES.desktopVideoUrl} className="w-full h-full object-cover" autoPlay playsInline loop muted={isMuted} data-id="element-111" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 pointer-events-none" data-id="element-112" />
        </div>

        {/* Desktop Controls Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col pointer-events-none" data-id="element-113">
          {/* Top Right Controls */}
          <div className="absolute top-16 right-8 flex flex-col items-end gap-4 pointer-events-auto" data-id="element-114">
            <button onClick={toggleMute} className="bg-black/50 backdrop-blur p-3 rounded-full hover:bg-black/70 transition text-white" data-id="element-115">
              <Volume2 className={`w-6 h-6 ${isMuted ? 'opacity-50' : 'opacity-100'}`} data-id="element-116" />
            </button>
          </div>

          {/* Bottom Right Controls */}
          <div className="absolute bottom-8 right-8 pointer-events-auto flex flex-col items-end gap-4 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 shadow-2xl" data-id="element-117">
            <button onClick={() => setFaqState('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-118">
              <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-119" />
              {t.faqBtn}
            </button>
            <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg px-8 py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-120">
              {t.continueBtn}
              <ChevronRight className="w-5 h-5" data-id="element-121" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-122">
      <PageNav current={2} total={5} onBack={onBack} onNext={faqState === 'none' ? onNext : undefined} data-id="element-123" />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col relative" data-id="element-124">
        {/* Video Area (Top 55%) */}
        <div className="absolute top-0 left-0 right-0 h-[55%] bg-black" data-id="element-125">
          <video ref={videoRef} src={IMAGES.videoUrl} className="w-full h-full object-cover" autoPlay playsInline loop muted={isMuted} data-id="element-126" />

          {/* Video Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80 pointer-events-none" data-id="element-127" />

          {/* FAQ Button moved to top */}
          <button onClick={() => setFaqState('menu')} className="absolute top-16 left-1/2 -translate-x-1/2 bg-[#82D600] px-6 py-2.5 rounded-full text-sm font-bold tracking-wide z-10 flex items-center gap-2 hover:bg-[#93e815] transition text-black shadow-lg shadow-[#82D600]/20 animate-pulse" data-id="element-128">
            <HelpCircle className="w-5 h-5" data-id="element-129" />
            {t.faqBtn}
          </button>

          <button onClick={toggleMute} className="absolute top-16 right-4 bg-black/50 backdrop-blur p-2 rounded-full z-10 hover:bg-black/70 transition" data-id="element-130">
            <Volume2 className={`w-4 h-4 ${isMuted ? 'opacity-50' : 'opacity-100'}`} data-id="element-131" />
          </button>
        </div>

        {/* Bottom Controls Area (Bottom 45%) */}
        <div className="absolute bottom-0 left-0 right-0 h-[45%] bg-black p-6 flex flex-col items-center text-center z-20 border-t border-zinc-800" data-id="element-132">
          {/* Subtitles at the top of the black area */}
          <div className="h-16 flex items-center justify-center mb-6 px-4 mt-2 w-full" data-id="element-133">
            <p className="text-lg font-medium text-white bg-black/80 px-4 py-2 rounded-lg border border-zinc-800/50 shadow-lg transition-opacity duration-500 max-w-[90%] leading-snug" data-id="element-134">
              {mockSubtitles[subtitleIndex]}
            </p>
          </div>

          <div className="flex-1 flex flex-col justify-center items-center w-full" data-id="element-135">
            <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-136">
              {t.continueBtn}
              <ChevronRight className="w-5 h-5" data-id="element-137" />
            </button>
          </div>

          {/* Logos at the bottom */}
          <div className="w-full flex justify-between items-end opacity-60 mt-auto pt-4" data-id="element-138">
            <div className="text-lg font-bold tracking-widest text-zinc-400" data-id="element-139">
              LEO
            </div>
            <div className="text-lg font-bold" data-id="element-140">
              ANZUPGO<sup className="text-[10px]" data-id="element-141">®</sup>
            </div>
          </div>
        </div>
      </div>

      {/* --- FAQ Overlays --- */}
    </div>;
}
function InfoScreen({
  t,
  onNext,
  onBack,
  isDesktop,
  setFaqState
}: {
  t: any;
  onNext: () => void;
  onBack: () => void;
  isDesktop?: boolean;
  setFaqState?: (state: FaqState) => void;
}) {
  const icons = [<Hand className="w-6 h-6" data-id="element-142" />, <Droplet className="w-6 h-6" data-id="element-143" />, <ClipboardList className="w-6 h-6" data-id="element-144" />, <MessageCircle className="w-6 h-6" data-id="element-145" />];
  if (isDesktop) {
    return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-146">
        <PageNav current={4} total={5} onBack={onBack} onNext={onNext} data-id="element-147" />

        <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-8 overflow-y-auto" data-id="element-148">
          <div className="max-w-4xl w-full text-center mb-6 lg:mb-8 mt-4" data-id="element-149">
            <h2 className="text-3xl lg:text-4xl font-semibold mb-3 lg:mb-4 leading-tight" data-id="element-150">
              {t.infoTitle}
            </h2>
            <p className="text-zinc-400 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto" data-id="element-151">
              {t.infoBody}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:gap-6 w-full max-w-4xl mb-8 lg:mb-10" data-id="element-152">
            {t.infoCards.map((card: any, idx: number) => <div key={idx} className="bg-white text-zinc-900 rounded-2xl lg:rounded-3xl p-5 lg:p-6 flex items-start gap-4 lg:gap-5 shadow-xl" data-id="element-153">
                <div className="bg-[#82D600]/20 p-3 lg:p-4 rounded-xl lg:rounded-2xl text-[#65a300] shrink-0" data-id="element-154">
                  {icons[idx]}
                </div>
                <div data-id="element-155">
                  <h3 className="font-bold text-lg lg:text-xl mb-1 lg:mb-2" data-id="element-156">
                    {card.title}
                  </h3>
                  <p className="text-zinc-600 text-sm lg:text-base leading-snug" data-id="element-157">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="w-full max-w-xs lg:max-w-sm shrink-0 flex flex-col gap-4" data-id="element-158">
            <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-159">
              <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-160" />
              {t.faqBtn}
            </button>
            <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg lg:text-xl py-3.5 lg:py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-161">
              {t.continueBtn}
              <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6" data-id="element-162" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-163">
      <PageNav current={4} total={5} onBack={onBack} onNext={onNext} data-id="element-164" />

      <div className="flex-1 overflow-y-auto p-6 pb-24 pt-20" data-id="element-165">
        <h2 className="text-3xl font-semibold mb-4 leading-tight" data-id="element-166">
          {t.infoTitle}
        </h2>
        <p className="text-zinc-400 mb-8 leading-relaxed" data-id="element-167">{t.infoBody}</p>

        <div className="grid grid-cols-1 gap-4" data-id="element-168">
          {t.infoCards.map((card: any, idx: number) => <div key={idx} className="bg-white text-zinc-900 rounded-2xl p-5 flex items-start gap-4 shadow-lg" data-id="element-169">
              <div className="bg-[#82D600]/20 p-3 rounded-xl text-[#65a300] shrink-0" data-id="element-170">
                {icons[idx]}
              </div>
              <div data-id="element-171">
                <h3 className="font-bold text-lg mb-1" data-id="element-172">{card.title}</h3>
                <p className="text-zinc-600 text-sm leading-snug" data-id="element-173">
                  {card.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a] to-transparent pt-12 flex flex-col gap-3" data-id="element-174">
        <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-175">
          <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-176" />
          {t.faqBtn}
        </button>
        <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-177">
          {t.continueBtn}
          <ChevronRight className="w-5 h-5" data-id="element-178" />
        </button>
      </div>
    </div>;
}
function RemindersScreen({
  t,
  onNext,
  onBack,
  isDesktop,
  setFaqState
}: {
  t: any;
  onNext: () => void;
  onBack: () => void;
  isDesktop?: boolean;
  setFaqState?: (state: FaqState) => void;
}) {
  if (isDesktop) {
    return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-179">
        <div className="absolute inset-0 bg-cover bg-center z-0 opacity-20" style={{
        backgroundImage: `url(${IMAGES.handsWarm})`,
        filter: 'grayscale(50%)'
      }} data-id="element-180" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a] z-10" data-id="element-181" />

        <PageNav current={3} total={5} onBack={onBack} onNext={onNext} data-id="element-182" />

        <div className="relative z-20 flex-1 flex flex-col items-center justify-center p-8" data-id="element-183">
          <h2 className="text-4xl lg:text-5xl font-semibold mb-8 lg:mb-12 leading-tight text-center" data-id="element-184">
            {t.remindersTitle}
          </h2>

          <div className="grid grid-cols-3 gap-6 lg:gap-8 w-full max-w-5xl" data-id="element-185">
            {t.reminders.map((item: any, idx: number) => <div key={idx} className="bg-[#2a2a2a]/80 backdrop-blur-md rounded-3xl p-6 lg:p-8 flex flex-col gap-4 lg:gap-6 border border-zinc-700/50 shadow-xl" data-id="element-186">
                <div className="text-[#82D600] text-5xl lg:text-6xl font-bold font-serif italic opacity-80 leading-none" data-id="element-187">
                  {idx + 1}
                </div>
                <div data-id="element-188">
                  <h3 className="font-semibold text-xl lg:text-2xl mb-2 lg:mb-3 text-white" data-id="element-189">
                    {item.title}
                  </h3>
                  <p className="text-zinc-400 text-base lg:text-lg leading-relaxed" data-id="element-190">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="mt-8 lg:mt-12 w-full max-w-md flex flex-col gap-4" data-id="element-191">
            <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-192">
              <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-193" />
              {t.faqBtn}
            </button>
            <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg lg:text-xl py-4 lg:py-5 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-194">
              {t.continueBtn}
              <ChevronRight className="w-6 h-6" data-id="element-195" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="relative w-full h-full flex flex-col" data-id="element-196">
      {/* Background Image */}
      <div className="absolute inset-0 bg-cover bg-center z-0 opacity-40" style={{
      backgroundImage: `url(${IMAGES.handsWarm})`,
      filter: 'grayscale(30%)'
    }} data-id="element-197" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a] z-10" data-id="element-198" />

      <PageNav current={3} total={5} onBack={onBack} onNext={onNext} data-id="element-199" />

      <div className="relative z-20 p-6 flex-1 flex flex-col justify-center pb-24 pt-20 overflow-y-auto" data-id="element-200">
        <h2 className="text-3xl font-semibold mb-8 leading-tight" data-id="element-201">
          {t.remindersTitle}
        </h2>

        <div className="space-y-4" data-id="element-202">
          {t.reminders.map((item: any, idx: number) => <div key={idx} className="bg-[#2a2a2a]/80 backdrop-blur-md rounded-2xl p-5 flex gap-4 border border-zinc-700/50" data-id="element-203">
              <div className="text-[#82D600] text-4xl font-bold font-serif italic opacity-80 shrink-0 leading-none" data-id="element-204">
                {idx + 1}
              </div>
              <div data-id="element-205">
                <h3 className="font-semibold text-lg mb-1 text-white" data-id="element-206">
                  {item.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed" data-id="element-207">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-[#1a1a1a] to-transparent pt-12 flex flex-col gap-3" data-id="element-208">
        <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-209">
          <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-210" />
          {t.faqBtn}
        </button>
        <button onClick={onNext} className="w-full bg-[#82D600] text-black font-semibold text-lg py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#93e815] transition-colors shadow-lg shadow-[#82D600]/20" data-id="element-211">
          {t.continueBtn}
          <ChevronRight className="w-5 h-5" data-id="element-212" />
        </button>
      </div>
    </div>;
}
function GoodbyeScreen({
  t,
  onFinish,
  onBack,
  isDesktop,
  setFaqState
}: {
  t: any;
  onFinish: () => void;
  onBack: () => void;
  isDesktop?: boolean;
  setFaqState?: (state: FaqState) => void;
}) {
  if (isDesktop) {
    return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-213">
        <PageNav current={5} total={5} onBack={onBack} onNext={undefined} data-id="element-214" />
        <div className="flex-1 flex flex-col items-center justify-center text-center max-w-2xl mx-auto p-8" data-id="element-215">
          <div className="w-20 lg:w-24 h-20 lg:h-24 bg-[#82D600]/10 rounded-full flex items-center justify-center mb-8 lg:mb-10" data-id="element-216">
            <Hand className="w-10 lg:w-12 h-10 lg:h-12 text-[#82D600]" data-id="element-217" />
          </div>

          <h2 className="text-4xl lg:text-5xl font-semibold mb-4 lg:mb-6 leading-tight" data-id="element-218">
            {t.goodbyeTitle}
          </h2>
          <p className="text-zinc-400 text-xl lg:text-2xl mb-12 lg:mb-16 leading-relaxed" data-id="element-219">
            {t.goodbyeBody}
          </p>

          <div className="w-full max-w-md flex flex-col gap-4" data-id="element-220">
            <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-221">
              <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-222" />
              {t.faqBtn}
            </button>
            <button onClick={onFinish} className="w-full bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg lg:text-xl py-4 lg:py-5 rounded-full hover:bg-zinc-800 transition-colors" data-id="element-223">
              {t.finishBtn}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto p-6 lg:p-8 border-t border-zinc-800 flex justify-between items-end" data-id="element-224">
          <p className="text-zinc-500 text-xs lg:text-sm max-w-lg text-left" data-id="element-225">
            {t.goodbyeDisclaimer}
          </p>

          <div className="flex flex-col items-end opacity-60" data-id="element-226">
            <div className="text-xl lg:text-2xl font-bold tracking-widest text-zinc-400" data-id="element-227">
              LEO
            </div>
            <div className="text-xl lg:text-2xl font-bold" data-id="element-228">
              ANZUPGO<sup className="text-xs lg:text-sm" data-id="element-229">®</sup>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="relative w-full h-full flex flex-col bg-[#1a1a1a]" data-id="element-230">
      <PageNav current={5} total={5} onBack={onBack} onNext={undefined} data-id="element-231" />
      <div className="flex-1 flex flex-col items-center justify-center text-center max-w-xs mx-auto p-6 pt-20 overflow-y-auto" data-id="element-232">
        <div className="w-20 h-20 bg-[#82D600]/10 rounded-full flex items-center justify-center mb-8" data-id="element-233">
          <Hand className="w-10 h-10 text-[#82D600]" data-id="element-234" />
        </div>

        <h2 className="text-3xl font-semibold mb-4 leading-tight" data-id="element-235">
          {t.goodbyeTitle}
        </h2>
        <p className="text-zinc-400 text-lg mb-12 leading-relaxed" data-id="element-236">
          {t.goodbyeBody}
        </p>

        <div className="w-full flex flex-col gap-3" data-id="element-237">
          <button onClick={() => setFaqState?.('menu')} className="w-full bg-[#2a2a2a] border border-zinc-600 px-6 py-3 rounded-full text-sm font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-700 transition text-white shadow-lg animate-pulse" data-id="element-238">
            <HelpCircle className="w-5 h-5 text-[#82D600]" data-id="element-239" />
            {t.faqBtn}
          </button>
          <button onClick={onFinish} className="w-full bg-[#2a2a2a] border border-zinc-700 text-white font-semibold text-lg py-4 rounded-full hover:bg-zinc-800 transition-colors" data-id="element-240">
            {t.finishBtn}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto p-6 border-t border-zinc-800" data-id="element-241">
        <p className="text-zinc-500 text-xs text-center mb-6 leading-relaxed" data-id="element-242">
          {t.goodbyeDisclaimer}
        </p>

        <div className="flex justify-between items-end opacity-60" data-id="element-243">
          <div className="text-xl font-bold tracking-widest text-zinc-400" data-id="element-244">
            LEO
          </div>
          <div className="text-xl font-bold" data-id="element-245">
            ANZUPGO<sup className="text-xs" data-id="element-246">®</sup>
          </div>
        </div>
      </div>
    </div>;
}